export type FirebaseAppTarget = 'home' | 'auto' | 'pet';

type EnvOptions = {
  app?: FirebaseAppTarget;
  optional?: boolean;
};

export type FirebaseClientEnv = {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  measurementId?: string;
};

export type FirebaseAdminEnv = {
  projectId: string;
  clientEmail: string;
  privateKey: string;
  storageBucket?: string;
};

const withAppSuffix = (key: string, app?: FirebaseAppTarget) =>
  app ? `${key}_${app.toUpperCase()}` : key;

const readEnv = (baseKey: string, { app, optional }: EnvOptions = {}) => {
  const envKey = withAppSuffix(baseKey, app);
  const fallbackKey = app ? baseKey : undefined;
  const value = process.env[envKey] ?? (fallbackKey ? process.env[fallbackKey] : undefined);

  if (!value && !optional) {
    const hint = fallbackKey && envKey !== fallbackKey ? ` (or ${fallbackKey})` : '';
    throw new Error(`Missing environment variable: ${envKey}${hint}`);
  }

  return value;
};

const normalizePrivateKey = (raw: string) => raw.replace(/\\n/g, '\n');

const readRequiredEnv = (key: string, options?: EnvOptions) => {
  const value = readEnv(key, options);
  if (!value) {
    throw new Error(`Missing environment variable: ${withAppSuffix(key, options?.app)}`);
  }
  return value;
};

export const getFirebaseClientEnv = (app?: FirebaseAppTarget): FirebaseClientEnv => {
  const measurementId = readEnv('NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID', { app, optional: true });

  return {
    apiKey: readRequiredEnv('NEXT_PUBLIC_FIREBASE_API_KEY', { app }),
    authDomain: readRequiredEnv('NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN', { app }),
    projectId: readRequiredEnv('NEXT_PUBLIC_FIREBASE_PROJECT_ID', { app }),
    storageBucket: readRequiredEnv('NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET', { app }),
    messagingSenderId: readRequiredEnv('NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID', { app }),
    appId: readRequiredEnv('NEXT_PUBLIC_FIREBASE_APP_ID', { app }),
    measurementId,
  };
};

export const getFirebaseAdminEnv = (app?: FirebaseAppTarget): FirebaseAdminEnv => {
  const privateKey = readRequiredEnv('FIREBASE_PRIVATE_KEY', { app });

  return {
    projectId: readRequiredEnv('FIREBASE_PROJECT_ID', { app }),
    clientEmail: readRequiredEnv('FIREBASE_CLIENT_EMAIL', { app }),
    privateKey: normalizePrivateKey(privateKey),
    storageBucket: readEnv('FIREBASE_STORAGE_BUCKET', { app, optional: true }),
  };
};

export const hasFirebaseClientEnv = (app?: FirebaseAppTarget) => {
  const apiKey = readEnv('NEXT_PUBLIC_FIREBASE_API_KEY', { app, optional: true });
  const authDomain = readEnv('NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN', { app, optional: true });
  const projectId = readEnv('NEXT_PUBLIC_FIREBASE_PROJECT_ID', { app, optional: true });
  const storageBucket = readEnv('NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET', { app, optional: true });
  const messagingSenderId = readEnv('NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID', { app, optional: true });
  const appId = readEnv('NEXT_PUBLIC_FIREBASE_APP_ID', { app, optional: true });

  return Boolean(apiKey && authDomain && projectId && storageBucket && messagingSenderId && appId);
};

